import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container, Box, Typography, Button, CircularProgress, Alert, Avatar, IconButton, Tooltip,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Chip, Skeleton,
    LinearProgress
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useFirebase } from '@/context/FirebaseContext';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ChatIcon from '@mui/icons-material/ChatBubbleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import Particles from '@tsparticles/react';
import { tsParticles } from '@tsparticles/engine';
import { loadSlim } from '@tsparticles/slim';

// Custom MUI Theme
const theme = createTheme({
    palette: {
        primary: { main: '#3b82f6' },
        secondary: { main: '#ec4899' },
        background: { default: '#1a0b2e' },
        text: { primary: '#e0e0e0', secondary: '#a855f7' },
        error: { main: '#ef4444' },
        success: { main: '#22c55e' },
        info: { main: '#a855f7' },
        warning: { main: '#f59e0b' },
    },
    typography: {
        fontFamily: '"Inter", sans-serif',
        h4: { fontWeight: 700, fontSize: 'clamp(1.5rem, 4vw, 1.75rem)' },
        h6: { fontWeight: 600, fontSize: 'clamp(0.875rem, 3vw, 1rem)' },
        body1: { fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)' },
        caption: { fontSize: 'clamp(0.625rem, 2vw, 0.75rem)' },
    },
});

// Styled Components
const MainContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    minHeight: '100vh',
    background: `linear-gradient(180deg, ${theme.palette.background.default}, #2e1a47)`,
    padding: theme.spacing(3, 1.5),
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2, 1),
    },
}));

const DashboardCard = styled(Box)(({ theme }) => ({
    background: 'rgba(59,44,91,0.9)',
    backdropFilter: 'blur(12px)',
    borderRadius: '12px',
    padding: theme.spacing(2),
    border: `2px solid ${theme.palette.secondary.main}33`,
    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },
}));

const BotCard = styled(Box)(({ theme }) => ({
    background: 'rgba(59,44,91,0.9)',
    backdropFilter: 'blur(12px)',
    borderRadius: '10px',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: `2px solid ${theme.palette.secondary.main}33`,
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${theme.palette.primary.main}66`,
        border: `2px solid ${theme.palette.secondary.main}cc`,
    },
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
}));

const CustomButton = styled(Button)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: '#fff',
    padding: 'clamp(0.4rem, 2vw, 0.6rem) clamp(1rem, 4vw, 1.5rem)',
    borderRadius: '10px',
    fontWeight: 600,
    fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
    textTransform: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: `linear-gradient(135deg, ${theme.palette.primary.main}cc, ${theme.palette.secondary.main}cc)`,
        boxShadow: `0 6px 16px rgba(0,0,0,0.4)`,
        transform: 'scale(1.05)',
    },
    '&:disabled': {
        background: `${theme.palette.primary.main}40`,
        color: 'rgba(255,255,255,0.3)',
        boxShadow: 'none',
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        padding: 'clamp(0.3rem, 2vw, 0.5rem) clamp(0.8rem, 4vw, 1.2rem)',
    },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
    background: 'rgba(59,44,91,0.9)',
    backdropFilter: 'blur(12px)',
    color: theme.palette.text.primary,
    padding: 'clamp(0.4rem, 2vw, 0.6rem) clamp(1rem, 4vw, 1.5rem)',
    borderRadius: '10px',
    fontWeight: 600,
    fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
    textTransform: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        background: `${theme.palette.text.secondary}66`,
        color: theme.palette.secondary.main,
        boxShadow: `0 6px 16px rgba(0,0,0,0.4)`,
        transform: 'scale(1.05)',
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        padding: 'clamp(0.3rem, 2vw, 0.5rem) clamp(0.8rem, 4vw, 1.2rem)',
    },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
    height: 24,
    fontSize: 'clamp(0.7rem, 2vw, 0.8rem)',
    fontWeight: 600,
    color: '#fff',
    background: 'rgba(59,44,91,0.9)',
    backdropFilter: 'blur(12px)',
    ...(status === 'active' && { background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.main}cc)` }),
    ...(status === 'configuring' && { background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.main}cc)` }),
    ...(status === 'pending_cag' && { background: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.main}cc)` }),
    ...(status === 'cag_generated' && { background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.main}cc)` }),
    ...(status === 'error' && { background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.main}cc)` }),
    ...(status === 'deleted' && { background: `linear-gradient(135deg, ${theme.palette.grey[600]}, ${theme.palette.grey[800]})` }),
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'scale(1.05)',
        boxShadow: `0 4px 12px rgba(0,0,0,0.4)`,
    },
    [theme.breakpoints.down('sm')]: {
        height: 22,
        fontSize: 'clamp(0.65rem, 2vw, 0.75rem)',
    },
}));

const ErrorAlert = styled(Alert)(({ theme }) => ({
    background: 'rgba(239, 68, 68, 0.15)',
    backdropFilter: 'blur(12px)',
    border: `1px solid ${theme.palette.error.main}66`,
    color: theme.palette.text.primary,
    borderRadius: '10px',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    '& .MuiAlert-icon': {
        color: theme.palette.text.primary,
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
}));

const NotificationAlert = styled(Alert)(({ theme }) => ({
    background: `linear-gradient(135deg, ${theme.palette.primary.main}33, ${theme.palette.secondary.main}33)`,
    backdropFilter: 'blur(12px)',
    border: `2px solid ${theme.palette.secondary.main}66`,
    color: theme.palette.text.primary,
    borderRadius: '12px',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    boxShadow: `0 8px 24px ${theme.palette.secondary.main}66`,
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.1)',
    '& .MuiLinearProgress-bar': {
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
    marginTop: theme.spacing(1),
}));

const LoadingSkeleton = () => (
    <BotCard sx={{ opacity: 0.7 }}>
        <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)', mr: 1.5 }} />
        <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" sx={{ fontSize: '0.875rem', bgcolor: 'rgba(255,255,255,0.1)' }} />
            <Skeleton variant="text" width="30%" sx={{ fontSize: '0.75rem', bgcolor: 'rgba(255,255,255,0.1)' }} />
        </Box>
        <Skeleton variant="rectangular" width={70} height={30} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
    </BotCard>
);

// Particles Configuration
const particlesInit = async () => {
    await loadSlim(tsParticles);
};

const particlesOptions = {
    particles: {
        number: { value: 40, density: { enable: true, value_area: 800 } },
        color: { value: ['#3b82f6', '#ec4899', '#a855f7', '#22c55e'] },
        shape: { type: 'circle' },
        opacity: { value: 0.5, random: true },
        size: { value: 4, random: true },
        move: {
            enable: true,
            speed: 1,
            direction: 'none',
            random: true,
            out_mode: 'out',
            trail: { enable: true, length: 8, intensity: 0.4 },
        },
    },
    interactivity: {
        events: { onhover: { enable: true, mode: 'repulse' }, onclick: { enable: true, mode: 'push' } },
        modes: { repulse: { distance: 100 }, push: { quantity: 2 } },
    },
};

// Constants
const DEFAULT_PLAN_NAME = 'Free Trial';
const REFRESH_INTERVAL = 10000; // 10 seconds
const COUNTDOWN_DURATION = 10; // Seconds

const BotManagementDashboard = () => {
    const { auth } = useFirebase();
    const navigate = useNavigate();
    const location = useLocation();

    const [bots, setBots] = useState([]);
    const [planStatus, setPlanStatus] = useState({ limits: { currentBots: 0, maxBots: 0 }, planName: DEFAULT_PLAN_NAME });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [isActionLoading, setIsActionLoading] = useState({ type: null, botId: null });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [botToDelete, setBotToDelete] = useState(null);
    const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
    const [newBotNotification, setNewBotNotification] = useState(null);
    const [countdown, setCountdown] = useState(null);

    const listBotsUrl = import.meta.env.VITE_LIST_BOTS_URL;
    const planStatusUrl = import.meta.env.VITE_GET_USER_PLAN_STATUS_URL;
    const deleteBotUrl = import.meta.env.VITE_DELETE_BOT_URL;

    const getHeaders = useCallback(async (contentType = 'application/json') => {
        const headers = { 'Content-Type': contentType };
        if (auth?.currentUser) {
            try {
                const idToken = await auth.currentUser.getIdToken(true);
                headers['Authorization'] = `Bearer ${idToken}`;
            } catch (tokenError) {
                console.error('Error retrieving ID token:', tokenError);
                throw new Error("Authentication token retrieval failed.");
            }
        } else {
            throw new Error("User not authenticated.");
        }
        return headers;
    }, [auth]);

    const fetchDashboardData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        setActionError(null);
        try {
            const headers = await getHeaders();
            const [botsResponse, planResponse] = await Promise.all([
                fetch(listBotsUrl, { headers }),
                fetch(planStatusUrl, { headers })
            ]);

            if (!botsResponse.ok) {
                const errData = await botsResponse.json().catch(() => ({}));
                throw new Error(errData.error || `Failed to fetch bots (${botsResponse.status})`);
            }
            const botsData = await botsResponse.json();
            setBots(botsData.bots || []);

            if (!planResponse.ok) {
                const errData = await planResponse.json().catch(() => ({}));
                throw new Error(errData.error || `Failed to fetch plan status (${planResponse.status})`);
            }
            const planData = await planResponse.json();
            setPlanStatus(planData.userPlan || { limits: { currentBots: 0, maxBots: 0 }, planName: DEFAULT_PLAN_NAME });

        } catch (err) {
            setError(`Failed to load dashboard: ${err.message}`);
            setBots([]);
            setPlanStatus({ limits: { currentBots: 0, maxBots: 0 }, planName: DEFAULT_PLAN_NAME });
        } finally {
            setIsLoading(false);
        }
    }, [getHeaders, listBotsUrl, planStatusUrl]);

    // Auto-refresh for pending bots
    useEffect(() => {
        const hasPendingBots = bots.some(bot => ['pending_cag', 'configuring'].includes(bot.status));
        if (hasPendingBots && !isLoading) {
            setCountdown(COUNTDOWN_DURATION);
            const timer = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        fetchDashboardData();
                        return COUNTDOWN_DURATION;
                    }
                    return prev - 1;
                });
            }, 1000);

            const refreshInterval = setInterval(() => {
                fetchDashboardData();
            }, REFRESH_INTERVAL);

            return () => {
                clearInterval(timer);
                clearInterval(refreshInterval);
            };
        }
    }, [bots, isLoading, fetchDashboardData]);

    // Handle new bot notification from location state
    useEffect(() => {
        if (location.state?.newBot) {
            setNewBotNotification({
                message: `🚀 Mind-Blowing Creation Alert! Your new bot "${location.state.newBot.name}" is being crafted! Hang tight, it’ll be ready soon!`,
                botId: location.state.newBot.botId,
            });
            setTimeout(() => setNewBotNotification(null), 10000); // Dismiss after 10s
        }
    }, [location.state]);

    useEffect(() => {
        if (auth?.currentUser) {
            fetchDashboardData();
        } else {
            setIsLoading(false);
            setError("Authentication required to view bots.");
        }
    }, [auth, fetchDashboardData]);

    useEffect(() => {
        if (location.state?.resumeDelete && location.state?.botId && location.state?.botName) {
            setBotToDelete({ botId: location.state.botId, botName: location.state.botName });
            setShowDeleteConfirm(true);
        }
    }, [location.state]);

    const handleDeleteBot = useCallback(async (botId) => {
        setActionError(null);
        setIsActionLoading({ type: 'delete', botId });
        try {
            const headers = await getHeaders();
            const response = await fetch(deleteBotUrl, {
                method: 'POST',
                headers,
                body: JSON.stringify({ botId }),
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.error || `Failed to delete bot (${response.status})`);
            }

            setBots(prevBots => prevBots.filter(bot => bot.botId !== botId));
            setPlanStatus(prev => ({
                ...prev,
                limits: { ...prev.limits, currentBots: Math.max(0, prev.limits.currentBots - 1) }
            }));

            if (sessionStorage.getItem('activeDocId') === botId) {
                sessionStorage.removeItem('activeDocId');
            }

        } catch (err) {
            setActionError(`Failed to delete bot: ${err.message}`);
        } finally {
            setIsActionLoading({ type: null, botId: null });
            setShowDeleteConfirm(false);
            setBotToDelete(null);
        }
    }, [getHeaders, deleteBotUrl]);

    const openDeleteConfirm = (bot) => {
        if (planStatus.planName.toLowerCase() === DEFAULT_PLAN_NAME.toLowerCase()) {
            setShowUpgradeDialog(true);
        } else {
            setBotToDelete(bot);
            setShowDeleteConfirm(true);
        }
    };

    const closeDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setBotToDelete(null);
    };

    const confirmDelete = () => {
        if (botToDelete) {
            handleDeleteBot(botToDelete.botId);
        }
    };

    const navigateToWizard = () => {
        if (planStatus.limits.currentBots >= planStatus.limits.maxBots) {
            setActionError(`Bot limit (${planStatus.limits.maxBots}) reached. Please upgrade or delete a bot.`);
            return;
        }
        navigate('/wizard');
    };

    const navigateToChat = (botId) => {
        sessionStorage.setItem('activeDocId', botId);
        navigate('/workflow/chat', { state: { activeDocId: botId } });
    };

    const handleManualRefresh = () => {
        fetchDashboardData();
        setCountdown(COUNTDOWN_DURATION);
    };

    const renderBotItem = (bot) => {
        const isDeleting = isActionLoading.botId === bot.botId && isActionLoading.type === 'delete';
        const canChat = ['active', 'cag_generated'].includes(bot.status);
        const isPending = ['pending_cag', 'configuring'].includes(bot.status);
        const progress = bot.status === 'pending_cag' ? 30 : bot.status === 'configuring' ? 70 : 100;

        return (
            <motion.div
                key={bot.botId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                role="listitem"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && canChat) navigateToChat(bot.botId);
                }}
            >
                <BotCard>
                    <Avatar
                        src={bot.botImageUrl || undefined}
                        sx={{
                            bgcolor: 'rgba(168, 85, 247, 0.2)',
                            width: 40,
                            height: 40,
                            mr: 1.5,
                            [theme.breakpoints.down('sm')]: { mb: 0.5 },
                        }}
                        aria-label={`Avatar for ${bot.botName || 'bot'}`}
                    >
                        {!bot.botImageUrl && bot.botName ? bot.botName.charAt(0).toUpperCase() : null}
                    </Avatar>
                    <Box sx={{ flex: 1, [theme.breakpoints.down('sm')]: { mb: 0.5 } }}>
                        <Typography
                            variant="body1"
                            sx={{ color: theme.palette.text.primary, fontWeight: 600 }}
                            aria-label={`Bot name: ${bot.botName || 'Bot'}`}
                        >
                            {bot.botName || `Bot ${bot.botId.substring(0, 6)}...`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                            <Tooltip title={`Status: ${bot.status || 'Unknown'}`}>
                                <StatusChip
                                    size="small"
                                    label={bot.status === 'cag_generated' ? 'Ready' : bot.status === 'pending_cag' ? 'Processing' : bot.status === 'configuring' ? 'Configuring' : bot.status || 'Unknown'}
                                    status={bot.status}
                                    sx={{ animation: isPending ? 'pulse 1.5s infinite' : 'none' }}
                                />
                            </Tooltip>
                            <Typography variant="caption" sx={{ color: theme.palette.text.primary }}>
                                {bot.createdAt ? `Created: ${new Date(bot.createdAt).toLocaleDateString()}` : ''}
                            </Typography>
                        </Box>
                        {isPending && (
                            <ProgressBar variant="determinate" value={progress} />
                        )}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        gap: 0.5,
                        [theme.breakpoints.down('sm')]: { width: '100%', justifyContent: 'flex-end' }
                    }}>
                        <Tooltip title={canChat ? "Chat with this bot" : `Bot is not ready (Status: ${bot.status || 'Unknown'})`}>
                            <span>
                                <IconButton
                                    aria-label={`Chat with ${bot.botName || 'bot'}`}
                                    onClick={() => navigateToChat(bot.botId)}
                                    disabled={!canChat || isDeleting}
                                    sx={{
                                        color: canChat ? theme.palette.primary.main : theme.palette.action.disabled,
                                        '&:hover': { bgcolor: `${theme.palette.primary.main}20` },
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                        padding: '6px',
                                    }}
                                >
                                    <ChatIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Delete bot">
                            <span>
                                <IconButton
                                    aria-label={`Delete ${bot.botName || 'bot'}`}
                                    onClick={() => openDeleteConfirm(bot)}
                                    disabled={isDeleting}
                                    sx={{
                                        color: theme.palette.error.main,
                                        '&:hover': { bgcolor: `${theme.palette.error.main}20` },
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                        padding: '6px',
                                    }}
                                >
                                    {isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </BotCard>
            </motion.div>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <MainContainer role="main">
                <Particles
                    id="dashboard-particles"
                    init={particlesInit}
                    options={particlesOptions}
                    style={{ position: 'absolute', top: 0, left: 0, zIndex: -1 }}
                />
                <Container maxWidth="lg">
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, flexWrap: 'wrap' }}>
                            <Typography
                                variant="h4"
                                sx={{ color: theme.palette.text.primary, textShadow: `0 0 8px ${theme.palette.text.secondary}66` }}
                                aria-label="Your Chatbots"
                            >
                                Your Chatbots
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: { xs: 1, sm: 0 } }}>
                                {countdown !== null && (
                                    <Tooltip title="Time until next refresh">
                                        <Typography
                                            variant="caption"
                                            sx={{ color: theme.palette.text.secondary }}
                                            aria-label={`Refresh in ${countdown} seconds`}
                                        >
                                            
                                        </Typography>
                                    </Tooltip>
                                )}
                                <Tooltip title="Refresh bot list">
                                    <CustomButton
                                        type="button"
                                        startIcon={<RefreshIcon />}
                                        onClick={handleManualRefresh}
                                        disabled={isLoading}
                                        sx={{ minWidth: 'auto' }}
                                        aria-label="Refresh bot list"
                                    >
                                        Refresh
                                    </CustomButton>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        planStatus.limits.currentBots >= planStatus.limits.maxBots
                                            ? `Bot limit (${planStatus.limits.maxBots}) reached`
                                            : "Create a new bot"
                                    }
                                >
                                    <span>
                                        <CustomButton
                                            type="button"
                                            startIcon={<AddIcon />}
                                            onClick={navigateToWizard}
                                            disabled={isLoading || planStatus.limits.currentBots >= planStatus.limits.maxBots}
                                            aria-label="Create new bot"
                                        >
                                            Create New Bot
                                        </CustomButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>

                        <DashboardCard>
                            <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 1.5 }} aria-label="Plan Status">
                                Plan Status
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1.5
                            }}>
                                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                                    Current Plan: <StatusChip
                                        label={planStatus.planName || (isLoading ? 'Loading...' : DEFAULT_PLAN_NAME)}
                                        size="small"
                                    />
                                </Typography>
                                <Typography variant="body1" sx={{ color: theme.palette.text.primary, display: 'flex', alignItems: 'center' }}>
                                    Usage: <Box component="span" sx={{ mr: 0.5 }} />
                                    <StatusChip
                                        label={`${planStatus.limits.currentBots} / ${planStatus.limits.maxBots}`}
                                        size="small"
                                    /> Bots
                                </Typography>
                            </Box>
                        </DashboardCard>

                        <AnimatePresence>
                            {newBotNotification && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.8 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <NotificationAlert
                                        severity="info"
                                        onClose={() => setNewBotNotification(null)}
                                        sx={{ position: 'relative' }}
                                        aria-label="New bot notification"
                                    >
                                        <Particles
                                            id="notification-particles"
                                            init={particlesInit}
                                            options={{
                                                ...particlesOptions,
                                                particles: { ...particlesOptions.particles, number: { value: 20 } }
                                            }}
                                            style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }}
                                        />
                                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                                            {newBotNotification.message}
                                        </Box>
                                    </NotificationAlert>
                                </motion.div>
                            )}
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <ErrorAlert severity="error" onClose={() => setError(null)}>
                                        {error}
                                    </ErrorAlert>
                                </motion.div>
                            )}
                            {actionError && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <ErrorAlert severity="warning" onClose={() => setActionError(null)}>
                                        {actionError}
                                    </ErrorAlert>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {isLoading ? (
                            <>
                                <LoadingSkeleton />
                                <LoadingSkeleton />
                                <LoadingSkeleton />
                            </>
                        ) : bots.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }} role="alert">
                                <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 1.5 }}>
                                    No bots created yet. Start your AI journey! (If created, wait or refresh.)
                                </Typography>
                                <Tooltip title="Create a new bot">
                                    <span>
                                        <CustomButton type="button" onClick={navigateToWizard} aria-label="Create your first bot">
                                            Create Your First Bot
                                        </CustomButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        ) : (
                            <Box role="list" aria-label="Bot list">
                                {bots.map(renderBotItem)}
                            </Box>
                        )}
                    </motion.div>
                </Container>

                <Dialog
                    open={showDeleteConfirm}
                    onClose={closeDeleteConfirm}
                    fullWidth
                    maxWidth="xs"
                    sx={{ zIndex: 1300 }}
                    PaperProps={{
                        sx: {
                            background: 'rgba(59,44,91,0.9)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: '12px',
                            border: `2px solid ${theme.palette.secondary.main}33`,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                            padding: theme.spacing(1),
                        }
                    }}
                    aria-labelledby="delete-dialog-title"
                    aria-describedby="delete-dialog-description"
                >
                    <DialogTitle id="delete-dialog-title" sx={{ color: theme.palette.text.primary, fontSize: 'clamp(1rem, 3vw, 1.125rem)' }}>
                        Confirm Deletion
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="delete-dialog-description" sx={{ color: theme.palette.text.primary, fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)' }}>
                            Are you sure you want to delete "{botToDelete?.botName || 'this bot'}"? This action is irreversible.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ pb: 1.5, justifyContent: 'center', gap: 1 }}>
                        <SecondaryButton type="button" onClick={closeDeleteConfirm} aria-label="Cancel deletion">
                            Cancel
                        </SecondaryButton>
                        <CustomButton
                            type="button"
                            onClick={confirmDelete}
                            disabled={isActionLoading.type === 'delete'}
                            sx={{
                                background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.main}cc)`,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${theme.palette.error.main}cc, ${theme.palette.error.main}99)`
                                }
                            }}
                            aria-label="Confirm delete bot"
                        >
                            {isActionLoading.type === 'delete' ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                "Delete Bot"
                            )}
                        </CustomButton>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={showUpgradeDialog}
                    onClose={() => setShowUpgradeDialog(false)}
                    fullWidth
                    maxWidth="xs"
                    sx={{ zIndex: 1300 }}
                    PaperProps={{
                        sx: {
                            background: 'rgba(59,44,91,0.9)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: '12px',
                            border: `2px solid ${theme.palette.secondary.main}33`,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                            padding: theme.spacing(1),
                        }
                    }}
                    aria-labelledby="upgrade-dialog-title"
                    aria-describedby="upgrade-dialog-description"
                >
                    <DialogTitle id="upgrade-dialog-title" sx={{ color: theme.palette.text.primary, fontSize: 'clamp(1rem, 3vw, 1.125rem)' }}>
                        Upgrade Required
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="upgrade-dialog-description" sx={{ color: theme.palette.text.primary, fontSize: 'clamp(0.75rem, 2.5vw, 0.875rem)' }}>
                            Bot deletion is only available on paid plans. Please upgrade your plan to enable this feature.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ pb: 1.5, justifyContent: 'center', gap: 1 }}>
                        <SecondaryButton type="button" onClick={() => setShowUpgradeDialog(false)} aria-label="Cancel upgrade">
                            Cancel
                        </SecondaryButton>
                        <CustomButton
                            type="button"
                            onClick={() => {
                                setShowUpgradeDialog(false);
                                navigate('/plans', {
                                    state: {
                                        from: '/workflow/bots',
                                        attemptedAction: 'delete',
                                        botId: botToDelete?.botId,
                                        botName: botToDelete?.botName
                                    }
                                });
                            }}
                            aria-label="View plans"
                        >
                            View Plans
                        </CustomButton>
                    </DialogActions>
                </Dialog>
            </MainContainer>
            <style jsx>{`
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
            `}</style>
        </ThemeProvider>
    );
};

export default BotManagementDashboard;